import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import Card from "../components/Card";
import { listBacktests } from "../api/backtests";
import {
  getTrackingList,
  putTrackingList,
  startMonitoring,
  stopMonitoring,
  type TrackedBacktestMeta,
} from "../api/tracking";
import { useTrackingWebSocket } from "../hooks/useTrackingWebSocket";
import type {
  SavedBacktestMeta,
  TrackingBacktestState,
  TrackingEntryToday,
  TrackingExitToday,
  TrackingPriorDayTrade,
  SymbolPrice,
} from "../state/types";

export default function TrackingPage() {
  const { signOut, idToken } = useAuth();
  const { state: wsState, connectionStatus, isStale } = useTrackingWebSocket(idToken);
  const [tracked, setTracked] = useState<TrackedBacktestMeta[]>([]);
  const [saved, setSaved] = useState<SavedBacktestMeta[]>([]);
  const [monitoring, setMonitoring] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const prevConnectionStatusRef = useRef<string>("disconnected");
  const skipNextMonitoringSyncRef = useRef(false);

  const loadLists = useCallback(async () => {
    setListError(null);
    try {
      const [trackedList, savedList] = await Promise.all([
        getTrackingList(),
        listBacktests(),
      ]);
      setTracked(trackedList);
      setSaved(savedList);
    } catch (e) {
      setListError(e instanceof Error ? e.message : "Failed to load lists");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  useEffect(() => {
    if (connectionStatus === "connected" && prevConnectionStatusRef.current !== "connected") {
      skipNextMonitoringSyncRef.current = true;
    }
    prevConnectionStatusRef.current = connectionStatus;
  }, [connectionStatus]);

  useEffect(() => {
    if (!wsState) return;
    if (skipNextMonitoringSyncRef.current) {
      skipNextMonitoringSyncRef.current = false;
      return;
    }
    setMonitoring(wsState.monitoring_on);
  }, [wsState?.monitoring_on]);

  const addToTracked = async (id: string) => {
    if (monitoring) return;
    setActionError(null);
    const next = [...tracked.map((b) => b.id), id];
    try {
      const updated = await putTrackingList(next);
      setTracked(updated);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed to add");
    }
  };

  const removeFromTracked = async (id: string) => {
    if (monitoring) return;
    setActionError(null);
    const next = tracked.filter((b) => b.id !== id).map((b) => b.id);
    try {
      const updated = await putTrackingList(next);
      setTracked(updated);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed to remove");
    }
  };

  const handleStart = async () => {
    setActionError(null);
    setActionLoading(true);
    try {
      const { warnings } = await startMonitoring();
      setMonitoring(true);
      if (warnings.length > 0) {
        setActionError(warnings.join("; "));
      }
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed to start monitoring");
    } finally {
      setActionLoading(false);
    }
  };

  const handleStop = async () => {
    setActionError(null);
    setActionLoading(true);
    try {
      await stopMonitoring();
      setMonitoring(false);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed to stop monitoring");
    } finally {
      setActionLoading(false);
    }
  };

  const trackedIds = new Set(tracked.map((b) => b.id));
  const availableToAdd = saved.filter((s) => !trackedIds.has(s.id));

  const backtests = wsState?.backtests ?? [];
  const allEntries: (TrackingEntryToday & { backtest: TrackingBacktestState })[] = [];
  const allExits: (TrackingExitToday & { backtest: TrackingBacktestState })[] = [];
  const allPrior: (TrackingPriorDayTrade & { backtest: TrackingBacktestState })[] = [];
  backtests.forEach((bt) => {
    bt.entries_today.forEach((e) => allEntries.push({ ...e, backtest: bt }));
    bt.exits_today.forEach((e) => allExits.push({ ...e, backtest: bt }));
    bt.prior_day_trades.forEach((p) => allPrior.push({ ...p, backtest: bt }));
  });

  /** PnL % for a closed exit (from return_fraction) or for display. */
  const formatPnlPct = (returnFraction: number | undefined): string => {
    if (returnFraction == null || Number.isNaN(returnFraction)) return "—";
    return `${(returnFraction * 100).toFixed(2)}%`;
  };
  /** Unrealized PnL % for prior-day open position: (current - entry) / entry for long, (entry - current) / entry for short. */
  const unrealizedPnlPct = (entryPrice: number, symbol: string, direction: string): number | undefined => {
    const cur = currentPrice(symbol, direction === "long" ? "long" : "short");
    if (cur == null || entryPrice <= 0) return undefined;
    const frac = direction === "long" ? (cur - entryPrice) / entryPrice : (entryPrice - cur) / entryPrice;
    return frac * 100;
  };

  /** Format entry date as YYYY-MM-DD plus " O" or " C" for entry at open or close. */
  const formatEntryDate = (entryTimestamp: string | undefined, entryType?: string): string => {
    if (!entryTimestamp) return "—";
    const dateOnly = entryTimestamp.slice(0, 10);
    const letter = entryType === "open" ? "O" : "C";
    return `${dateOnly} ${letter}`;
  };

  const getPrice = (symbol: string): SymbolPrice => wsState?.prices?.[symbol] ?? {};
  const currentPrice = (symbol: string, side: "long" | "short"): number | undefined => {
    const p = getPrice(symbol);
    if (side === "long") return p.bid ?? p.last;
    return p.ask ?? p.last;
  };

  return (
    <div className="min-h-screen bg-newsprint text-ink">
      <header className="bg-newsprint-panel border-b border-rule p-6">
        <div className="max-w-screen-xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-3xl font-semibold text-ink mb-0 font-headline shrink-0">
              CountingTrades.com
            </h1>
            <Link to="/app" className="text-sm underline hover:no-underline text-ink/80 shrink-0">
              Back to backtest
            </Link>
            <span className="text-ink/50 shrink-0" aria-hidden>
              |
            </span>
            <span className="text-sm text-ink/70 font-headline shrink-0">Trade tracking</span>
          </div>
          <button
            type="button"
            onClick={signOut}
            className="text-sm underline hover:no-underline text-ink/80"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-6 py-8 space-y-8">
        {connectionStatus === "disconnected" && (
          <div className="bg-amber-100 dark:bg-amber-900/30 border border-amber-600 text-amber-800 dark:text-amber-200 px-4 py-2 rounded" role="alert">
            Disconnected from server. Reconnecting…
          </div>
        )}
        {connectionStatus === "connecting" && (
          <div className="bg-ink/10 text-ink/80 px-4 py-2 rounded">
            Connecting…
          </div>
        )}
        {monitoring && !wsState?.has_cli && (
          <div className="bg-amber-100 dark:bg-amber-900/30 border border-amber-600 text-amber-800 dark:text-amber-200 px-4 py-2 rounded" role="alert">
            Monitoring is on but no CLI is connected. Start the CLI and connect to TWS to receive prices.
          </div>
        )}
        {isStale && (
          <div className="bg-ink/10 text-ink/80 px-4 py-2 rounded">
            No price update recently; data may be stale.
          </div>
        )}
        <section>
          <Card>
            <h2 className="text-xl font-semibold mb-4 font-headline">Tracking list</h2>
            {loading ? (
              <p className="text-ink/80">Loading…</p>
            ) : listError ? (
              <p className="text-red-600">{listError}</p>
            ) : (
              <>
                <p className="text-sm text-ink/70 mb-3">
                  Backtests in this list are monitored for real-time trade signals when monitoring is
                  on. When monitoring is active, you cannot change the list.
                </p>
                <div className="flex flex-wrap gap-4 items-start">
                  <div>
                    <p className="text-sm font-medium text-ink/80 mb-2">Tracked</p>
                    <ul className="list-none space-y-1">
                      {tracked.length === 0 ? (
                        <li className="text-ink/60 text-sm">None</li>
                      ) : (
                        tracked.map((b) => (
                          <li key={b.id} className="flex items-center gap-2">
                            <span className="text-ink">
                              {b.name}
                              {b.groupLabel ? ` (${b.groupLabel})` : ""}
                            </span>
                            <button
                              type="button"
                              disabled={monitoring}
                              onClick={() => removeFromTracked(b.id)}
                              className="text-sm underline hover:no-underline text-ink/80 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Remove
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-ink/80 mb-2">Add from saved</p>
                    {availableToAdd.length === 0 ? (
                      <p className="text-ink/60 text-sm">All saved backtests are tracked or none saved.</p>
                    ) : (
                      <ul className="list-none space-y-1">
                        {availableToAdd.map((b) => (
                          <li key={b.id} className="flex items-center gap-2">
                            <span className="text-ink">
                              {b.name}
                              {b.groupLabel ? ` (${b.groupLabel})` : ""}
                            </span>
                            <button
                              type="button"
                              disabled={monitoring}
                              onClick={() => addToTracked(b.id)}
                              className="text-sm underline hover:no-underline text-ink/80 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Add
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-4">
                  <button
                    type="button"
                    disabled={actionLoading || monitoring}
                    onClick={handleStart}
                    className="px-4 py-2 bg-ink text-newsprint rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Start monitoring
                  </button>
                  <button
                    type="button"
                    disabled={actionLoading || !monitoring}
                    onClick={handleStop}
                    className="px-4 py-2 border border-rule text-ink rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Stop monitoring
                  </button>
                </div>
                {actionError && (
                  <p className="mt-3 text-sm text-amber-700 dark:text-amber-400" role="alert">
                    {actionError}
                  </p>
                )}
              </>
            )}
          </Card>
        </section>

        <section>
          <Card>
            <h2 className="text-xl font-semibold mb-4 font-headline">Active trade entries (today)</h2>
            <p className="text-ink/70 text-sm">
              Trades signaled to enter at today’s open or close. Updates when monitoring is on. If the
              opening or closing price is not yet official, it is marked as “provisional”.
            </p>
            {allEntries.length === 0 ? (
              <div className="mt-3 text-ink/60 text-sm">No entries yet.</div>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-rule text-left">
                      <th className="py-2 pr-4">Symbol</th>
                      <th className="py-2 pr-4">Backtest</th>
                      <th className="py-2 pr-4">Current price</th>
                      <th className="py-2 pr-4">Trigger price</th>
                      <th className="py-2">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allEntries.map((e, i) => {
                      const sym = e.backtest.primary_symbol;
                      const entryType = e.backtest.entry_type === "open" ? "O" : "C";
                      const isOpen = e.backtest.entry_type === "open";
                      const official = isOpen
                        ? getPrice(sym).open_is_official
                        : getPrice(sym).close_is_official;
                      const provisional = official === false;
                      return (
                        <tr key={i} className="border-b border-rule/50">
                          <td className="py-2 pr-4">{sym}</td>
                          <td className="py-2 pr-4">
                            {e.backtest.name}
                            {e.backtest.groupLabel ? ` (${e.backtest.groupLabel})` : ""}
                          </td>
                          <td className="py-2 pr-4">
                            {currentPrice(sym, e.trade_direction === "long" ? "long" : "short") ?? "—"}
                          </td>
                          <td className="py-2 pr-4">
                            {e.entry_price}
                            {provisional && (
                              <span
                                className="ml-1 text-ink/50 text-xs"
                                title={isOpen ? "Opening price not yet official" : "Closing price not yet official"}
                              >
                                (provisional)
                              </span>
                            )}
                          </td>
                          <td className="py-2">{entryType}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </section>

        <section>
          <Card>
            <h2 className="text-xl font-semibold mb-4 font-headline">Active trade exits (today)</h2>
            <p className="text-ink/70 text-sm">
              Open positions that have an exit signal today. Updates when monitoring is on.
            </p>
            {allExits.length === 0 ? (
              <div className="mt-3 text-ink/60 text-sm">No exits yet.</div>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-rule text-left">
                      <th className="py-2 pr-4">Symbol</th>
                      <th className="py-2 pr-4">Backtest</th>
                      <th className="py-2 pr-4">Entry date</th>
                      <th className="py-2 pr-4">Entry price</th>
                      <th className="py-2 pr-4">Exit price</th>
                      <th className="py-2 pr-4">Current price</th>
                      <th className="py-2 pr-4">PnL (%)</th>
                      <th className="py-2">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allExits.map((e, i) => {
                      const entryType = e.backtest.entry_type === "open" ? "O" : "C";
                      return (
                        <tr key={i} className="border-b border-rule/50">
                          <td className="py-2 pr-4">{e.backtest.primary_symbol}</td>
                          <td className="py-2 pr-4">
                            {e.backtest.name}
                            {e.backtest.groupLabel ? ` (${e.backtest.groupLabel})` : ""}
                          </td>
                          <td className="py-2 pr-4">{formatEntryDate(e.entry_timestamp, e.backtest.entry_type)}</td>
                          <td className="py-2 pr-4">{e.entry_price}</td>
                          <td className="py-2 pr-4">{e.exit_price}</td>
                          <td className="py-2 pr-4">
                            {getPrice(e.backtest.primary_symbol).last ?? "—"}
                          </td>
                          <td className="py-2 pr-4">{formatPnlPct(e.return_fraction)}</td>
                          <td className="py-2">{entryType}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </section>

        <section>
          <Card>
            <h2 className="text-xl font-semibold mb-4 font-headline">Active trades from prior days</h2>
            <p className="text-ink/70 text-sm">
              Positions opened on earlier days, not yet exited. Updates when monitoring is on.
            </p>
            {allPrior.length === 0 ? (
              <div className="mt-3 text-ink/60 text-sm">No prior-day trades.</div>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-rule text-left">
                      <th className="py-2 pr-4">Symbol</th>
                      <th className="py-2 pr-4">Backtest</th>
                      <th className="py-2 pr-4">Entry date</th>
                      <th className="py-2 pr-4">Entry price</th>
                      <th className="py-2 pr-4">PnL (%)</th>
                      <th className="py-2">Exit type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allPrior.map((p, i) => {
                      const pnl = unrealizedPnlPct(p.entry_price, p.backtest.primary_symbol, p.trade_direction);
                      return (
                        <tr key={i} className="border-b border-rule/50">
                          <td className="py-2 pr-4">{p.backtest.primary_symbol}</td>
                          <td className="py-2 pr-4">
                            {p.backtest.name}
                            {p.backtest.groupLabel ? ` (${p.backtest.groupLabel})` : ""}
                          </td>
                          <td className="py-2 pr-4">{formatEntryDate(p.entry_timestamp, p.backtest.entry_type)}</td>
                          <td className="py-2 pr-4">{p.entry_price}</td>
                          <td className="py-2 pr-4">{pnl != null ? `${pnl.toFixed(2)}%` : "—"}</td>
                          <td className="py-2">{p.exit_type === "open" ? "O" : "C"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </section>
      </main>
    </div>
  );
}
